<template>
  <div class="flex flex-col gap-4 pb-4 md:gap-8 md:pb-8">
    <slot></slot>
  </div>
</template>

<script setup lang="ts">
import { fixImageUrlProtocol } from '@/utils/contentfulHelpers'

const props = withDefaults(
  defineProps<{
    title?: string
    description?: string
    cloudinaryImage?: any
    ogType?: 'website' | 'article' | 'book' | 'profile'
  }>(),
  {
    title: undefined,
    description: undefined,
    cloudinaryImage: undefined,
    ogType: 'website',
  },
)

const siteUrl = useRuntimeConfig().public.SITE_URL
const route = useRoute()

// Get meta information
const metaTitle = computed(() => props.title)
const metaDescription = computed(() => props.description)

// Use useAsyncData with proper SSR handling
const { data: metaImageData } = await useAsyncData(
  `meta-image-${route.path}`,
  async () => {
    if (props.cloudinaryImage) {
      return fixImageUrlProtocol(props.cloudinaryImage[0]?.url)
    }
    return new URL('/share.png', siteUrl).toString()
  },
  {
    // Cache the result to avoid repeated requests
    default: () => new URL('/share.png', siteUrl).toString(),
  },
)

const metaImage = computed(() => metaImageData.value)

const canonicalUrl = computed(() => {
  return new URL(route.path, siteUrl).toString()
})

useSeoMeta({
  title: metaTitle.value,
  description: metaDescription.value,
  ogTitle: metaTitle.value,
  ogDescription: metaDescription.value,
  ogImage: metaImage.value,
  ogImageSecureUrl: metaImage.value,
  ogImageAlt: metaTitle.value,
  ogUrl: canonicalUrl.value,
  ogType: props.ogType,
  twitterCard: 'summary_large_image',
  twitterTitle: metaTitle.value,
  twitterDescription: metaDescription.value,
  twitterImage: metaImage.value,
})

// Set canonical URL
useHead({
  link: [
    {
      rel: 'canonical',
      href: canonicalUrl.value,
    },
  ],
})
</script>
